<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-wrench-clock</v-icon>
        <span class="px-2">{{ $t('repair_centers') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="showCreateDialog"></create-button>
      </v-card-title>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="services"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.image="{ item }">
          <v-img
            v-if="item.image_url"
            :src="getImageUrl(item.image_url)"
            max-width="80px"
            max-height="50px"
            contain
            class="my-1 rounded border"
          ></v-img>
          <span v-else class="text-muted text-caption">No Image</span>
        </template>
        <template v-slot:item.name="{ item }">
          <div>
            <strong>{{ item.title_ar }}</strong>
            <div v-if="item.title_en" class="text-caption text-muted">{{ item.title_en }}</div>
          </div>
        </template>
        <template v-slot:item.address="{ item }">
          <div>
            {{ item.address_ar || '-' }}
            <div v-if="item.address_en" class="text-caption text-muted">{{ item.address_en }}</div>
          </div>
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip :color="item.is_active ? 'success' : 'error'" dark small>
            {{ item.is_active ? $t('active') : $t('inactive') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mx-2" @click="editItem(item)">mdi-pencil</v-icon>
          <v-icon small class="mx-2" @click="deleteItem(item, services.indexOf(item))">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="700px">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ isEdit ? $t('edit_repair_center') : $t('add_repair_center') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <!-- Title AR (Required) -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.title_ar"
                    :rules="[v => !!v || 'Arabic Name is required']"
                    :label="$t('service_name_ar') + '*'"
                    required
                  ></v-text-field>
                </v-col>
                <!-- Title EN -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.title_en"
                    :label="$t('service_name_en')"
                  ></v-text-field>
                </v-col>

                <!-- Description AR -->
                <v-col cols="12" sm="6">
                  <v-textarea
                    v-model="editedItem.description_ar"
                    :label="$t('description_ar')"
                    rows="2"
                  ></v-textarea>
                </v-col>
                <!-- Description EN -->
                <v-col cols="12" sm="6">
                  <v-textarea
                    v-model="editedItem.description_en"
                    :label="$t('description_en')"
                    rows="2"
                  ></v-textarea>
                </v-col>

                <!-- Phone -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.phone"
                    :label="$t('phone')"
                  ></v-text-field>
                </v-col>
                <!-- WhatsApp -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.whatsapp"
                    :label="$t('whatsapp')"
                  ></v-text-field>
                </v-col>

                <!-- Address AR -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.address_ar"
                    :label="$t('address_ar')"
                  ></v-text-field>
                </v-col>
                <!-- Address EN -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.address_en"
                    :label="$t('address_en')"
                  ></v-text-field>
                </v-col>

                <!-- Location URL -->
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.location_url"
                    :label="$t('location_url')"
                    :rules="[v => !v || /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(v) || 'Must be a valid URL']"
                  ></v-text-field>
                </v-col>

                <!-- Latitude -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.latitude"
                    type="number"
                    step="any"
                    label="Latitude"
                  ></v-text-field>
                </v-col>
                <!-- Longitude -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.longitude"
                    type="number"
                    step="any"
                    label="Longitude"
                  ></v-text-field>
                </v-col>

                <!-- Working Hours AR -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.working_hours_ar"
                    :label="$t('working_hours_ar')"
                  ></v-text-field>
                </v-col>
                <!-- Working Hours EN -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.working_hours_en"
                    :label="$t('working_hours_en')"
                  ></v-text-field>
                </v-col>

                <!-- Rating -->
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedItem.rating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    :rules="[v => !v || (v >= 0 && v <= 5) || 'Rating must be between 0 and 5']"
                    :label="$t('rating')"
                  ></v-text-field>
                </v-col>
                <!-- Sort Order -->
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedItem.sort_order"
                    type="number"
                    min="0"
                    :label="$t('sort_order')"
                  ></v-text-field>
                </v-col>
                <!-- Status -->
                <v-col cols="12" sm="4">
                  <v-switch
                    v-model="editedItem.is_active"
                    :label="$t('is_active')"
                  ></v-switch>
                </v-col>

                <!-- Service Image -->
                <v-col cols="12">
                  <!-- New Selected File Preview -->
                  <div v-if="newImagePreview" class="mb-3 d-flex align-center">
                    <v-img
                      :src="newImagePreview"
                      max-width="120"
                      max-height="90"
                      contain
                      class="rounded border me-3"
                    ></v-img>
                    <div>
                      <div class="text-caption text-success mb-1">New Image Preview</div>
                      <v-btn color="warning" outlined x-small @click="cancelNewImage">
                        Cancel New Image
                      </v-btn>
                    </div>
                  </div>

                  <!-- Existing Current Image -->
                  <div v-else-if="isEdit && editedItem.image_url && !removeImage" class="mb-3 d-flex align-center">
                    <v-img
                      :src="getImageUrl(editedItem.image_url)"
                      max-width="120"
                      max-height="90"
                      contain
                      class="rounded border me-3"
                    ></v-img>
                    <div>
                      <div class="text-caption text-muted mb-1">Current Image</div>
                      <v-btn color="error" outlined small @click="removeImage = true">
                        <v-icon left small>mdi-delete</v-icon>
                        Remove Image
                      </v-btn>
                    </div>
                  </div>

                  <!-- Removed Image State -->
                  <div v-else-if="removeImage" class="mb-2 text-caption text-error">
                    <v-icon color="error" small>mdi-alert-circle</v-icon>
                    Image will be removed upon saving.
                    <v-btn text x-small color="primary" @click="removeImage = false">Undo</v-btn>
                  </div>

                  <v-file-input
                    v-model="imageFile"
                    accept="image/*"
                    :label="$t('service_image')"
                    show-size
                    prepend-icon="mdi-camera"
                    @change="onFileChange"
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="blue darken-1" text @click="save">{{ $t('Save Changes') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import CreateButton from "@/components/CreateButton";

export default {
  components: { CreateButton },
  data() {
    return {
      services: [],
      isLoading: false,
      search: "",
      dialog: false,
      valid: true,
      isEdit: false,
      imageFile: null,
      newImagePreview: null,
      removeImage: false,
      editedItem: {
        id: null,
        service_type: "repair_center",
        title_ar: "",
        title_en: "",
        description_ar: "",
        description_en: "",
        phone: "",
        whatsapp: "",
        address_ar: "",
        address_en: "",
        location_url: "",
        latitude: null,
        longitude: null,
        working_hours_ar: "",
        working_hours_en: "",
        rating: 0,
        sort_order: 0,
        is_active: true
      },
      headers: [
        { text: "id", value: "id", align: "start" },
        { text: "service_image", value: "image", sortable: false },
        { text: "Name", value: "name" },
        { text: "phone", value: "phone" },
        { text: "whatsapp", value: "whatsapp" },
        { text: "address_ar", value: "address" },
        { text: "rating", value: "rating" },
        { text: "sort_order", value: "sort_order" },
        { text: "is_active", value: "is_active" },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page")
      };
    }
  },
  mounted() {
    this.loadServices();
  },
  methods: {
    getImageUrl(url) {
      if (!url) return null;
      if (typeof url !== "string") return null;
      if (url.includes("/backend/storage/")) {
        return url.replace("/backend/storage/", "/storage/");
      }
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        const clean = url.replace(/^\/?(backend\/)?(storage\/)?/, "");
        return `https://beepgo.net/storage/${clean}`;
      }
      return url;
    },
    loadServices() {
      this.isLoading = true;
      axios
        .get("/driver-services?service_type=repair_center")
        .then(response => {
          this.services = response.data;
        })
        .catch(error => {
          this.$notify({
            title: this.$t("Error"),
            text: "Failed to load repair centers",
            type: "error"
          });
          console.error(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    onFileChange(file) {
      if (file) {
        this.imageFile = file;
        this.removeImage = false;
        this.newImagePreview = URL.createObjectURL(file);
      } else {
        this.imageFile = null;
        this.newImagePreview = null;
      }
    },
    cancelNewImage() {
      this.imageFile = null;
      this.newImagePreview = null;
    },
    showCreateDialog() {
      this.isEdit = false;
      this.imageFile = null;
      this.newImagePreview = null;
      this.removeImage = false;
      this.editedItem = {
        id: null,
        service_type: "repair_center",
        title_ar: "",
        title_en: "",
        description_ar: "",
        description_en: "",
        phone: "",
        whatsapp: "",
        address_ar: "",
        address_en: "",
        location_url: "",
        latitude: null,
        longitude: null,
        working_hours_ar: "",
        working_hours_en: "",
        rating: 0,
        sort_order: 0,
        is_active: true
      };
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    editItem(item) {
      this.isEdit = true;
      this.imageFile = null;
      this.newImagePreview = null;
      this.removeImage = false;
      this.editedItem = Object.assign({}, item);
      this.editedItem.is_active = !!item.is_active;
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    deleteItem(item, index) {
      this.$swal
        .fire({
          title: this.$t("yes_delete"),
          text: "Are you sure you want to delete this repair center?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel")
        })
        .then(result => {
          if (result.isConfirmed) {
            axios
              .delete(`/driver-services/${item.id}`)
              .then(() => {
                this.services.splice(index, 1);
                this.$notify({
                  title: this.$t("Success"),
                  text: "Repair center deleted successfully",
                  type: "success"
                });
              })
              .catch(error => {
                this.$swal(this.$t("Error"), "Failed to delete service", "error");
              });
          }
        });
    },
    save() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        this.dialog = false;

        const formData = new FormData();
        if (this.isEdit) {
          formData.append("_method", "PUT");
        }
        formData.append("service_type", "repair_center");
        formData.append("title_ar", this.editedItem.title_ar);
        formData.append("title_en", this.editedItem.title_en || "");
        formData.append("description_ar", this.editedItem.description_ar || "");
        formData.append("description_en", this.editedItem.description_en || "");
        formData.append("phone", this.editedItem.phone || "");
        formData.append("whatsapp", this.editedItem.whatsapp || "");
        formData.append("address_ar", this.editedItem.address_ar || "");
        formData.append("address_en", this.editedItem.address_en || "");
        formData.append("location_url", this.editedItem.location_url || "");
        
        if (this.editedItem.latitude !== null && this.editedItem.latitude !== "") {
          formData.append("latitude", this.editedItem.latitude);
        }
        if (this.editedItem.longitude !== null && this.editedItem.longitude !== "") {
          formData.append("longitude", this.editedItem.longitude);
        }
        
        formData.append("working_hours_ar", this.editedItem.working_hours_ar || "");
        formData.append("working_hours_en", this.editedItem.working_hours_en || "");
        formData.append("rating", this.editedItem.rating !== null ? this.editedItem.rating : 0);
        formData.append("sort_order", this.editedItem.sort_order || 0);
        formData.append("is_active", this.editedItem.is_active ? 1 : 0);

        if (this.imageFile) {
          formData.append("image", this.imageFile);
        } else if (this.removeImage) {
          formData.append("remove_image", "1");
        }

        const url = this.isEdit
          ? `/driver-services/${this.editedItem.id}`
          : "/driver-services";

        axios
          .post(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          })
          .then((response) => {
            const updated = response.data && response.data.data ? response.data.data : null;
            if (updated) {
              if (updated.image_url) {
                updated.image_url = updated.image_url + '?v=' + Date.now();
              }
              const idx = this.services.findIndex(s => s.id === updated.id);
              if (idx !== -1) {
                this.$set(this.services, idx, updated);
              } else {
                this.loadServices();
              }
            } else {
              this.loadServices();
            }
            this.$notify({
              title: this.$t("Success"),
              text: this.isEdit ? "Repair center updated successfully" : "Repair center created successfully",
              type: "success"
            });
          })
          .catch(error => {
            let msg = error.response?.data?.message || "Save failed";
            this.$swal(this.$t("Error"), msg, "error");
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    }
  }
};
</script>
